"use client"

import styled from "@emotion/styled"
import { Box } from "@mui/material"
import { LottieRefCurrentProps } from "lottie-react"
import dynamic from "next/dynamic"
import React, { Suspense, useEffect, useState } from "react"
import { useInView } from "react-intersection-observer"

// Use Next.js dynamic import for lottie-react, SSR off for client-only
const Lottie = dynamic(() => import("lottie-react"), { ssr: false })

interface LottiePlayerProps {
  lottieUrl: string
  width?: number | string
  height?: number | string
  style?: React.CSSProperties
  className?: string
  playOnce?: boolean
  loop?: boolean
  autoplay?: boolean
  ariaLabel?: string
}

const StyledBox = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

/**
 * LottiePlayer - Optimized Lottie animation player for Hachshara blocks
 * - Loads Lottie JSON only when in view
 * - Plays once by default, can be set to loop
 * - Suspends loading until in view for performance
 * - Accepts width/height or inherits parent size
 */
const LottiePlayer = ({
  lottieUrl,
  width = "100%",
  height = "100%",
  style,
  className,
  playOnce = true,
  loop = false,
  autoplay = true,
  ariaLabel,
}: LottiePlayerProps) => {
  const { ref, inView } = useInView({ triggerOnce: false, threshold: 0.2 })
  const [animationData, setAnimationData] = useState<any>(null)
  const [hasPlayed, setHasPlayed] = useState(false)
  const lottieInstance = React.useRef<LottieRefCurrentProps | null>(null)

  useEffect(() => {
    if (inView && !animationData) {
      fetch(lottieUrl)
        .then((res) => res.json())
        .then((data) => setAnimationData(data))
        .catch(() => setAnimationData(null))
    }
  }, [inView, lottieUrl, animationData])

  // Play only once logic
  const handleComplete = () => {
    if (playOnce) {
      setHasPlayed(true)
      // Go to last frame and stay there
      if (lottieInstance.current && animationData && animationData.op) {
        // animationData.op is the total number of frames
        lottieInstance.current.goToAndStop(animationData.op - 1, true)
      }
    }
  }

  return (
    <StyledBox
      ref={ref}
      style={{ width, height, ...style }}
      className={className}
      aria-label={ariaLabel}
      data-testid="lottie-player"
    >
      {inView && animationData ? (
        <Suspense fallback={<div style={{ width: "100%", height: "100%" }} />}>
          <Lottie
            animationData={animationData}
            loop={loop}
            autoplay={autoplay}
            onComplete={handleComplete}
            style={{ width: "100%", height: "100%" }}
            lottieRef={lottieInstance}
          />
        </Suspense>
      ) : (
        <div style={{ width: "100%", height: "100%" }} />
      )}
    </StyledBox>
  )
}

export default LottiePlayer
